import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { TextData } from '../../../shared/dto/EntityRepository';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { textBuilder } from '../../../shared/converter/entityBuilder';
import { useUnsavedChanges } from '../../../shared/hooks/useUnsavedChanges';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import { useTextListActions, useTextListState } from '../../../shared/hooks/useApi/useTextList';
import EmptyTextList from '../EmptyTextList';
import { useTextListStore } from '../../../shared/store/TextListStore';
import { useTextViewStore } from '../../../shared/store/TextViewStore';
import { useTextListSelection } from '../../../shared/hooks/useTextListSelection';
import { useTextIndexing } from '../../../shared/hooks/useTextIndexing';
import HintWrapper from '../../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../../shared/constants/hintMessages';
import createIcon from '../../../asset/icon/create.png';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { useDeviceStore } from '../../../shared/store/deviceStore';
import { DeviceType } from '../../../shared/enum/EnumRepository';
import { useTextListMarquee } from '../../../shared/hooks/useTextListMarquee';
import reorderIcon from '../../../asset/icon/reorder.png';
import { usePageOwnerIdentityStore } from '../../../shared/store/pageOwnerIdentityStore';
import { useParams } from 'react-router-dom';
import { isPageOwnerMatchedToRaw } from '../../../shared/util/pageOwnerMatch';
import { canShowTextList } from '../../../shared/util/visibility/textListVisibility';
import type { ResourceScope } from '../../../shared/util/scopedResource';
import { useTextTagStore } from '../../../shared/store/textTagStore';
import { toTextListFilterData } from '../../../shared/util/textTag';
import TextInfoFixed from './TextInfoFixed';
import PageAboutTextFixed from './PageAboutTextFixed';

const TextList: React.FC = () => {
  const { isOwner, user } = useAuth();
  const { isEditMode } = useEditMode();
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const { textList, listOwnerAui, listScope, status } = useTextListState();
  const { getTextList, getTextListOwner, createText } = useTextListActions();
  const pageSize = useTextListStore((s) => s.pageSize);
  const sortData = useTextListStore((s) => s.sortData);
  const selectedTags = useTextTagStore((s) => s.selectedTags);
  const { runAfterUnsavedChangesCheck } = useUnsavedChanges();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShowList = canShowTextList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    listScope,
    currentScope,
    status,
  });
  const visibleTextList = canShowList ? textList : [];
  const {
    handleTextClick,
    handleKeyDown,
    commitMarqueeHitTextsPreview,
    handleTextListBackgroundClick,
  } = useTextListSelection(visibleTextList);
  const textListRef = useRef<HTMLDivElement>(null);
  const textListCompRef = useRef<HTMLElement>(null);

  const hasMultiSelected = useTextViewStore((s) => s.multiSelectedText.length > 0);
  const { isIndexingMode, setIsIndexingMode } = useTextIndexing();

  const marqueeVisualEnabled = isEditMode && !isMobile;
  const {
    handleTextListMouseDown,
    handleTextListClickCapture,
    marqueeBox,
    marqueeSelecting,
  } = useTextListMarquee({
    textList: visibleTextList,
    textListCompRef,
    commitMarqueeHitTextsPreview,
    enabled: marqueeVisualEnabled,
  });


  useEffect(() => {
    if (textListRef.current) {
      // 컨테이너에 포커스 설정
      textListRef.current.focus();
    }
  }, [textListRef]);

  useEffect(() => {
    const getTextListWithApi = async () => {
      if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;

      const filterData = toTextListFilterData(selectedTags);
      if (isOwner) {
        await getTextListOwner({ page: 1, size: pageSize, sortData: sortData, filterData });
      } else {
        await getTextList({ page: 1, size: pageSize, sortData: sortData, filterData });
      }
    }
    getTextListWithApi();
  }, [raw, pageOwner?.aui, pageSize, sortData, isOwner, selectedTags]);


  const handleCreateText = async () => {
    if (!user) return;
    runAfterUnsavedChangesCheck(async () => {
      await createText(textBuilder());
    });
  };

  const handleReorderTextList = () => {
    if (!isEditMode) return;
    if (isMobile) return;
    if (isIndexingMode) {
      runAfterUnsavedChangesCheck(() => setIsIndexingMode(false));
      return;
    }
    setIsIndexingMode(true);
  };

  return (
    <ListRail>
      <Space />
      <TextListContainer
        tabIndex={0}
        ref={textListRef}
        onKeyDown={handleKeyDown}>
        <TextListComp
          ref={textListCompRef}
          onMouseDown={handleTextListMouseDown}
          onClickCapture={handleTextListClickCapture}
          onClick={handleTextListBackgroundClick}
          $marqueeSelecting={marqueeSelecting}
        >
          {visibleTextList.map((each: TextData) =>
            <TextInfoFixed
              key={each.id}
              data={each}
              handleTextClick={handleTextClick}
            />
          )}
          {canShowList && visibleTextList.length === 0 && <EmptyTextList />}
          {marqueeBox && (
            <MarqueeSelectionBox
              style={{
                left: marqueeBox.left,
                top: marqueeBox.top,
                width: marqueeBox.width,
                height: marqueeBox.height,
              }}
            />
          )}
          {isEditMode &&
            <BtnContainer data-suppress-marquee>
              {!isIndexingMode && (
                <HintWrapper content={getHintMessage('CREATE_TEXT')}>
                  <UniversalIconBtn
                    size="standard"
                    border={true}
                    backgroundImg={createIcon}
                    onClick={handleCreateText} />
                </HintWrapper>
              )}
              <HintWrapper content={getHintMessage('REORDER_TEXT_LIST')}>
                <UniversalIconBtn
                  size="standard"
                  border={true}
                  backgroundImg={reorderIcon}
                  onClick={handleReorderTextList}
                />
              </HintWrapper>
            </BtnContainer>
          }
        </TextListComp>
        <PageAboutTextFixed />
      </TextListContainer>
    </ListRail>
  );
}

const ListRail = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-left: clamp(20px, 2.5vw, 40px);
  flex-shrink: 0;
`;

const Space = styled.div`
  flex: 1;
  min-height: 0;
`;

const TextListContainer = styled.article`
  width: clamp(300px, 30vw, 500px);
  min-height: calc(30vh);
  display: flex;
  flex-direction: column;
  outline: none;
  position: relative;
  margin-bottom: calc(8vh);
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_02};
`;

const TextListComp = styled.section<{ $marqueeSelecting?: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  position: relative;
  overflow-y: auto;
  user-select: ${({ $marqueeSelecting }) => ($marqueeSelecting ? 'none' : 'auto')};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MarqueeSelectionBox = styled.div`
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  backdrop-filter: blur(1px);
  z-index: 3;
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: clamp(25px, 3vw, 50px);
  right: clamp(50px, 8vw, 70px);
  width: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5vw;
  z-index: 4;
`;

export default TextList;
